import MarketStateStatDateCompetitorSnapshotLowestItemIndicesPerRoomMappingType
	from '@app/model/market_state/MarketStateStatDateCompetitorSnapshotLowestItemIndicesPerRoomMappingType';
import {
	MarketStateStatDateCompetitorSnapshotLowestItemPerHotelIdAndRoomIds,
} from '@app/model/market_state/MarketStateStatDateCompetitorSnapshotLowestItemPerHotelIdAndRoomIds';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import CompetitorType from '@app/model/company_hotel/CompetitorType';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import MarketStateStatDateCompetitorPmsAvailability
	from '@app/model/market_state/MarketStateStatDateCompetitorPmsAvailability';
import MarketStateStatDateCompetitorAvailabilityEvolutionItem
	from '@app/model/market_state/MarketStateStatDateCompetitorAvailabilityEvolutionItem';

export class MarketStateStatDateCompetitorSnapshot {
	competitorId: number;
	isGroup: boolean;
	isMainHotel: boolean;
	pmsAvailability: MarketStateStatDateCompetitorPmsAvailability;
	lowestItemIndex: number;
	lowestItemIndicesPerRoomMappingType: MarketStateStatDateCompetitorSnapshotLowestItemIndicesPerRoomMappingType[];
	lowestItemPerHotelIdAndRoomIds: MarketStateStatDateCompetitorSnapshotLowestItemPerHotelIdAndRoomIds[];
	availabilityEvolutionItems: MarketStateStatDateCompetitorAvailabilityEvolutionItem[];

	constructor(data: MarketStateStatDateCompetitorSnapshot) {
		Object.assign(this, data);
		if (data.lowestItemPerHotelIdAndRoomIds) {
			this.lowestItemPerHotelIdAndRoomIds = data.lowestItemPerHotelIdAndRoomIds.map(item => new MarketStateStatDateCompetitorSnapshotLowestItemPerHotelIdAndRoomIds(item));
		} else data.lowestItemPerHotelIdAndRoomIds = [];
		if (data.availabilityEvolutionItems) {
			this.availabilityEvolutionItems = data.availabilityEvolutionItems.map(item => new MarketStateStatDateCompetitorAvailabilityEvolutionItem(item));
		} else this.availabilityEvolutionItems = [];
	}

	public getLowestItemForRoom() {
		return this.lowestItemPerHotelIdAndRoomIds[this.lowestItemIndex];
	};

	public getLowestItemForRoomMappingType(roomMappingType: HotelRoomMappingType) {
		const lowestItemIndex = this.lowestItemIndicesPerRoomMappingType.find(item => item.roomMappingType === roomMappingType);
		if(!!lowestItemIndex) {
			return this.lowestItemPerHotelIdAndRoomIds[lowestItemIndex.index];
		}
		return undefined;
	}

	public isAvailable() {
		const lowestItemForRoom = this.getLowestItemForRoom();
		return !!lowestItemForRoom && lowestItemForRoom.isAvailable();
	}

	public isSoldOut() {
		const lowestItemForRoom = this.getLowestItemForRoom();
		return !!lowestItemForRoom && lowestItemForRoom.isSoldOut();
	}

	public isLos() {
		const lowestItemForRoom = this.getLowestItemForRoom();
		return !!lowestItemForRoom && lowestItemForRoom.isLos();
	}

	public isSoldOutAndNotLos() {
		const lowestItemForRoom = this.getLowestItemForRoom();
		return !!lowestItemForRoom && lowestItemForRoom.isSoldOutAndNotLos();
	}

	public isEmpty() {
		const lowestItemForRoom = this.getLowestItemForRoom();
		return !lowestItemForRoom || lowestItemForRoom.isEmpty();
	}

	public isNotAvailable() {
		const lowestItemForRoom = this.getLowestItemForRoom();
		return !lowestItemForRoom || lowestItemForRoom.isNotAvailable();
	}

	public isNotSupported() {
		const lowestItemForRoom = this.getLowestItemForRoom();
		return !!lowestItemForRoom && lowestItemForRoom.isNotSupported;
	}

	public groupLowestItemsPerHotelId() {
		return this.lowestItemPerHotelIdAndRoomIds.reduce((acc, item) => {
			acc[item.hotelId] = (acc[item.hotelId] || []).concat(item);
			return acc;
		}, {} as { [hotelId: number]: MarketStateStatDateCompetitorSnapshotLowestItemPerHotelIdAndRoomIds[] });
	}

	public ratioAvailableHotelsOverTotal(companyHotel: CompanyHotel) {
		const groupLowestItemsPerHotelId = this.groupLowestItemsPerHotelId();
		const totalHotels = companyHotel.findCompetitorById(this.competitorId)?.competitors.length??0;
		const availableHotels = Object.values(groupLowestItemsPerHotelId)
			.filter(items => items.every(item => !!item.lowestItem && (item.lowestItem.isAvailable || item.lowestItem.isLos))).length;
		return [availableHotels, totalHotels];
	}

	public countTotalAvailability() {
		return this.lowestItemPerHotelIdAndRoomIds.reduce((acc, item) => {
			acc += item?.availability??0;
			return acc;
		}, 0);
	}

	public countAvailabilityEvolutionTotal() {
		return this.availabilityEvolutionItems.reduce((acc, item) => {
			acc += item?.availability ?? 0;
			return acc;
		}, 0);
	}

	public sumAvailabilityPerRoom() {
		return this.lowestItemPerHotelIdAndRoomIds.reduce((acc, item) => {
			acc[item.roomId] = (acc[item.roomId] || 0) + item.availability;
			return acc;
		}, {} as { [roomId: number]: number });
	}

	public getHotelName(companyHotel: CompanyHotel) {
		if(companyHotel.mainHotel.id === this.competitorId)
			return companyHotel.mainHotel.competitors[0].hotel.name;

		const competitor = companyHotel.findCompetitorById(this.competitorId);
		if (!competitor || competitor.type !== CompetitorType.HOTEL) return '';
		else return competitor.competitors[0].hotel.name;
	}

	public getCompetitor(companyHotel: CompanyHotel) {
		return companyHotel.findCompetitorById(this.competitorId);
	}
}